import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';
import UserSearchField from '@wix/da-shared-react/pkg/UserSearchField';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import Tag from '@wix/da-ds/pkg/TagList/Tag';
import CloseIcon from '@wix/da-ds/pkg/Icons/CloseBold';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { useSelector } from 'react-redux';

import s from './UserInput.scss';

export interface Props {
  users: PapiUser[];
  candidates?: PapiUser[];
  className?: string;
  scrollContainerClassName?: string;
  prefix?: React.ReactNode;
  placeholder?: string;
  onKeyDown?: (e) => void;
  onAddUser: (user: PapiUser) => void;
  onRemoveUser: (user: PapiUser) => void;
  onNoMatchingUsers?: (input: string) => void;
  onRemoveAll: () => void;
  withClearButton?: boolean;
  /** Users to exclude from the user search in addition to those in `users` */
  additionalExcludedUsers?: PapiUser[];
  disabled?: boolean;
}

export const UserInput: React.FC<Props> = ({
  users,
  className,
  candidates,
  scrollContainerClassName,
  prefix,
  placeholder,
  onAddUser,
  onRemoveUser,
  onRemoveAll,
  onKeyDown,
  onNoMatchingUsers,
  withClearButton,
  disabled,
  additionalExcludedUsers,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentUser = useSelector(getCurrentUser);

  const renderUser = (user: PapiUser, index: number) => (
    <Tag
      key={`selected-${user.userId}-${index}`}
      size="small"
      postfix={<CloseIcon size={IconSize.TINY} />}
      onClick={() => {
        onRemoveUser(user);
      }}
    >
      {user.username}
    </Tag>
  );

  const mergedExcludedUsers = useMemo(
    () => [currentUser, ...users, ...(additionalExcludedUsers || [])],
    [users, additionalExcludedUsers, currentUser]
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLInputElement;
      if (
        e.key === 'Backspace' &&
        target.value.length === 0 &&
        users.length > 0
      ) {
        onRemoveUser(users[users.length - 1]);
      }
      onKeyDown && onKeyDown(e);
    },
    [users, onRemoveUser, onKeyDown]
  );

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [users.length]);

  return (
    <div
      className={classnames(s['root'], className)}
      onClick={e => inputRef.current && inputRef.current.focus()}
    >
      {prefix && (
        // We're expecting the client to provide a label for this field, so hide this prefix to avoid
        // additional noise
        <span className={s['prefix']} aria-hidden="true">
          {prefix}
        </span>
      )}
      <Flex
        className={classnames(
          s['item-container'] as any,
          prefix && s['has-prefix'],
          scrollContainerClassName
        )}
        alignItems="flex-start"
        wrap="wrap"
        innerRef={scrollAreaRef}
      >
        {users.map(renderUser)}
        <PopupContext.Provider
          value={{ isInPopup: true, setScrollableContentRef: noop }}
        >
          <UserSearchField
            key="searchField"
            candidates={candidates}
            onUserSelect={onAddUser}
            excludedUsers={mergedExcludedUsers}
            className={classnames(s['user-input'], s['item-flex'])}
            inputClassName={s['user-input-textfield']}
            placeholder={placeholder}
            inputRef={inputRef}
            withClearButton={withClearButton}
            showClearButton={withClearButton && users.length > 0}
            onClear={onRemoveAll}
            onInputKeyDown={onInputKeyDown}
            onNoMatchingUsers={onNoMatchingUsers}
            popupFixedPosition
            isChatContext
            disabled={disabled}
          />
        </PopupContext.Provider>
      </Flex>
    </div>
  );
};
UserInput.displayName = 'UserInput';

export default UserInput;
